import { Fragment, useState } from 'react';
import { useTranslation } from 'react-i18next';
import CopyToClipboard from 'react-copy-to-clipboard';
import { NotificationManager } from 'react-notifications';
import { useDispatch, useSelector } from 'react-redux';
import { Button, Icon, Input, Message, Modal, Popup } from 'semantic-ui-react';
import moment from 'moment';
import QRCode from 'qrcode.react';
import html2canvas from 'html2canvas';

import {
  EvalPlanDisable,
  ShowEvaluationQuestion,
} from '../../../../components';

import { printableSurvey } from '../../../../store/actions';
import { getEvaluationPlanBaseName } from '../../../../utils/evaluationPlan';
import editIcon from '../../../../assets/edit-icon.svg';

import {
  ContentAndHeaderEvaluation,
  getSurveyLink,
  getPrintableSurveyToken,
} from '../../../../utils';

import { UserTextService } from '../../../../services';
import { map, get } from '../../../../utils/lodash';
import { INSTANCE_WITH_CULTURE_COUNTS } from '../../../../utils/instanceConstant';

import '../activity.scss';
import '../../activities.scss';
import './surveyContainer.scss';

// TODO:i18n
const formatEvaluationResponseLabel = (t, response) =>
  response === t('survey.self_assessed') ? t('survey.mgr_assessed') : response;

const SurveyContainer = ({
  evalPlan,
  currentActivity,
  updateEvaluationPlanName,
  pageDisabled,
  ...props
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  // const [evalVisibility, setEvalVisibility] = useState(false);
  const surveyValidation = moment(currentActivity.eventStartDate) > moment();

  const [facilityVisibility, setFacilityVisibility] = useState(false);
  const [questionVisibility, setQuestionVisibility] = useState(false);
  const [qrCodeModal, setQrCodeModal] = useState(false);
  const [isSingleLink, setIsSingleLink] = useState(true);
  const [isSurveyTitleModalOpen, setIsSurveyTitleModalOpen] = useState(false);
  const [surveyTitleValue, setSurveyTitleValue] = useState(
    evalPlan.surveyTitle || currentActivity.name || ''
  );
  const [surveyTitleError, setSurveyTitleError] = useState(null);

  const handleSurveyTitleSave = async () => {
    if (!surveyTitleValue.trim()) {
      setSurveyTitleError(t('survey.errors.title_required'));
      return;
    }
    setSurveyTitleError(null);
    await updateEvaluationPlanName(
      currentActivity.id,
      evalPlan.id,
      {
        name: getEvaluationPlanBaseName(evalPlan),
        surveyTitle: surveyTitleValue,
      },
      t('survey.title_used_on_survey'),
      t('survey.title_used_on_survey_updated_successfully')
    );
    setIsSurveyTitleModalOpen(false);
  };
  const downloadSurveyLoading = useSelector(
    (state) => state.survey.printableSurveyLoading
  );

  const surveyLink = getSurveyLink(
    currentActivity.id,
    evalPlan.id,
    map(
      get(evalPlan, 'evaluationQuestions', []),
      (evaluation) => evaluation.objectiveId
    ),
    true
  );
  const kioskLink = getSurveyLink(
    currentActivity.id,
    evalPlan.id,
    map(
      get(evalPlan, 'evaluationQuestions', []),
      (evaluation) => evaluation.objectiveId
    )
  );

  const isSelfAssesed =
    get(evalPlan, 'method.response', '') === 'Self-assessed';
  const isCultureCount =
    get(evalPlan, 'method.response', '') === 'Participant' &&
    get(evalPlan, 'method.method', '') === 'Intercept';
  const facilitatorNote = get(evalPlan, 'method.facilitatorNote', null);

  const evaluationQs = get(evalPlan, 'evaluationQuestions', null);
  const seriesParentId = get(evalPlan, 'seriesParentId', null);
  const isSeriesActive = get(evalPlan, 'isSeriesActive', null);
  const dateMeasured = get(evalPlan, 'dateMeasured', null);

  const downloadQRCode = async () => {
    html2canvas(document.getElementById('qrCodeContainer'), { scale: 2 }).then(
      (canvas) => {
        const qrCodeURL = canvas
          .toDataURL('image/png')
          .replace('image/png', 'image/octet-stream');
        let aEl = document.createElement('a');
        aEl.href = qrCodeURL;
        aEl.download = 'QR_Code.png';
        document.body.appendChild(aEl);
        aEl.click();
        document.body.removeChild(aEl);
      }
    );
  };

  const downloadSurvey = async () => {
    const pdfWindow = window.open('', '_blank');
    if (pdfWindow) {
      pdfWindow.document.write(
        `<!doctype html>
        <html>
          <head>
            <title>Survey Download</title>
            <style>
              html, body {
                width: 100%;
                height: 100%;
                margin: 0;
                font-family: Roboto, 'Helvetica Neue', Arial, sans-serif;
              }

              body {
                display: flex;
                align-items: center;
                justify-content: center;
                color: #333;
              }
            </style>
          </head>
          <body>
            <div>Preparing survey download...</div>
          </body>
        </html>`
      );
      pdfWindow.document.close();
    }

    try {
      const token = await getPrintableSurveyToken(
        currentActivity.id,
        evalPlan.id,
        map(
          get(evalPlan, 'evaluationQuestions', []),
          (evaluation) => evaluation.objectiveId
        ),
        true
      );
      const result = await dispatch(printableSurvey(token));
      if (!result.s3Url) throw new Error(t('survey.missing_survey_pdf_url'));

      if (pdfWindow) {
        pdfWindow.location.href = result.s3Url;
      } else {
        window.open(result.s3Url, '_blank', 'noopener,noreferrer');
      }
    } catch (error) {
      if (pdfWindow) {
        pdfWindow.document.body.innerHTML = `<div>${t('survey.unable_to_prepare_survey_download')}</div>`;
      }
      console.error('error: ', error);
    }
  };

  const GenerateQrModal = (props) => {
    const name = get(props.evalPlan, 'name', 'Evaluation plan');
    return (
      <div className="survey-modal">
        <Modal
          size="mini"
          open={qrCodeModal}
          closeIcon={
            <Icon
              onClick={() => setQrCodeModal(false)}
              name="close"
              className="closeicon"
            />
          }
        >
          <Modal.Header>{t('survey.survey_qr_code')}</Modal.Header>
          <Modal.Content id="qrCodeContainer">
            <div className="w-100 text-center justify-content-center py-1">
              <div>
                <p className="qr-planName">{name}</p>
                <QRCode
                  id="qrCode"
                  size={270}
                  value={isSingleLink ? surveyLink : kioskLink}
                />
                <span className="qr-activityId">
                  <p>#{props.id}</p>
                </span>
              </div>
            </div>
          </Modal.Content>
          <Modal.Actions>
            <div className="model-buttons">
              <Button
                className="modal-cancel"
                type="button"
                onClick={() => setQrCodeModal(false)}
              >
                {t('actions.cancel')}
              </Button>
              <Button
                onClick={() => downloadQRCode()}
                content={t('actions.download')}
                className="modal-proceed"
              />
            </div>
          </Modal.Actions>
        </Modal>
      </div>
    );
  };

  return (
    <>
      {!pageDisabled && (
        <div className="url-card-links row">
          <div className="link-div">
            <div className="url-link-container">
              <div className="link-title survey-title-label">
                <Popup
                  trigger={<span>{t('activity.evaluation.survey_title')}</span>}
                  content={t('survey.title_tooltip')}
                  position="top left"
                  size="small"
                />
              </div>
              <div className="survey-title-header">
                <span className="survey-title-text">{surveyTitleValue}</span>
                <Modal
                  className="evalation-model"
                  open={isSurveyTitleModalOpen}
                  onOpen={() => setIsSurveyTitleModalOpen(true)}
                  closeIcon={
                    <Icon
                      onClick={() => {
                        setIsSurveyTitleModalOpen(false);
                        setSurveyTitleError(null);
                      }}
                      name="close"
                      className="closeicon"
                    />
                  }
                  size="small"
                  trigger={
                    <div className="evaluation-edit-icon">
                      <img className="icons-edit" src={editIcon} alt="edit" />
                    </div>
                  }
                >
                  <Modal.Header>{t('survey.edit_title')}</Modal.Header>
                  <Modal.Content>
                    <div className="evaluation-label">
                      {t('activity.evaluation.survey_title')}
                    </div>
                    <Input
                      fluid
                      type="text"
                      className="evaluation-heading-input"
                      onChange={(e) => {
                        setSurveyTitleValue(e.target.value);
                        if (surveyTitleError) setSurveyTitleError(null);
                      }}
                      value={surveyTitleValue}
                      error={!!surveyTitleError}
                    />
                    {surveyTitleError && (
                      <div className="survey-title-error">
                        {surveyTitleError}
                      </div>
                    )}
                  </Modal.Content>
                  <Modal.Actions>
                    <Button
                      className="modal-cancel"
                      onClick={() => setIsSurveyTitleModalOpen(false)}
                    >
                      {t('actions.cancel')}
                    </Button>
                    <Button
                      className="modal-proceed"
                      onClick={handleSurveyTitleSave}
                    >
                      {t('actions.save')}
                    </Button>
                  </Modal.Actions>
                </Modal>
              </div>
            </div>
          </div>
        </div>
      )}
      <div className="link-row">
        {get(evalPlan, 'isDisabled', false) ? (
          <EvalPlanDisable {...evalPlan} />
        ) : (
          <Fragment>
            {dateMeasured ? (
              <></>
            ) : (seriesParentId && isSeriesActive) || seriesParentId == null ? (
              isSelfAssesed ? null : isCultureCount ? (
                // TAK-2088: no "Go to Culture Counts" link once the instance
                // no longer offers it; nothing else is offered for these plans
                INSTANCE_WITH_CULTURE_COUNTS && (
                  <div className="culture-count-container ml-4 mt-3">
                    <label className="eval-plan-label">
                      {t('survey.select_data_collection_method')}
                    </label>
                    <a
                      href={'https://culturecounts.cc/sign-in'}
                      rel="noopener noreferrer"
                      target="_blank"
                      className="culture-count-a"
                    >
                      <div className="culture-count">
                        <span>
                          {t('survey.go_to_culture_counts')}
                          <Icon
                            name="external alternate"
                            className="ml-3 mb-1"
                          />
                        </span>
                      </div>
                    </a>
                  </div>
                )
              ) : (
                <Fragment>
                  <div className="url-card-links row">
                    <div className="link-div">
                      <div className="url-link-container">
                        <div className="link-title">
                          {t('survey.printable_survey')}
                        </div>
                        <div className="link-button">
                          <Button
                            className="copy-link-btn"
                            loading={downloadSurveyLoading}
                            size="small"
                            onClick={() => {
                              downloadSurvey();
                            }}
                          >
                            <Icon
                              size="large"
                              name="file alternate outline"
                              className="copy-icon"
                            />
                            {t('survey.download_survey_pdf')}
                          </Button>
                        </div>
                      </div>
                      <div className="url-link-container">
                        <div className="link-title">
                          {t('survey.single_use')}
                        </div>
                        <div className="link-button">
                          <CopyToClipboard
                            text={surveyLink}
                            onCopy={() =>
                              NotificationManager.info(
                                t('survey.copied_to_clipboard'),
                                t('survey.survey_link')
                              )
                            }
                          >
                            <Button className="copy-link-btn" size="small">
                              <Icon size="large" name="clone outline" />
                              {t('survey.copy_survey_question_link')}
                            </Button>
                          </CopyToClipboard>
                          <a
                            rel="noopener noreferrer"
                            href={surveyLink}
                            target="_blank"
                          >
                            <Button className="survey-button" size="small">
                              <Icon
                                size="large"
                                name="file alternate outline"
                              />
                              {t('survey.go_to_survey')}
                            </Button>
                          </a>
                          <Button
                            className="survey-button"
                            size="small"
                            onClick={() => {
                              setIsSingleLink(true);
                              setQrCodeModal(true);
                            }}
                          >
                            <Icon size="large" name="qrcode" />
                            {t('survey.generate_qr_code')}
                          </Button>
                        </div>
                      </div>
                      <div className="url-link-container">
                        <div className="link-title">
                          {t('survey.multiple_use')}
                        </div>
                        <div className="link-button">
                          <CopyToClipboard
                            text={kioskLink}
                            onCopy={() =>
                              NotificationManager.info(
                                t('survey.copied_to_clipboard'),
                                t('survey.survey_link')
                              )
                            }
                          >
                            <Button className="copy-link-btn" size="small">
                              <Icon
                                size="large"
                                name="clone outline"
                                className="copy-icon"
                              />
                              {t('survey.copy_survey_question_link')}
                            </Button>
                          </CopyToClipboard>
                          <a
                            rel="noopener noreferrer"
                            href={kioskLink}
                            target="_blank"
                          >
                            <Button className="survey-button" size="small">
                              <Icon
                                size="large"
                                name="file alternate outline"
                                className="copy-icon"
                              />
                              {t('survey.go_to_survey')}
                            </Button>
                          </a>
                          <Button
                            className="survey-button"
                            size="small"
                            onClick={() => {
                              setIsSingleLink(false);
                              setQrCodeModal(true);
                            }}
                          >
                            <Icon size="large" name="qrcode" />
                            {t('survey.generate_qr_code')}
                          </Button>
                        </div>
                      </div>
                    </div>
                    <div className="obj-div col-lg-6"></div>
                  </div>
                </Fragment>
              )
            ) : !isSelfAssesed ? (
              <div className="m-4">
                <div className="msg-series-evaluation">
                  <Message
                    color="yellow"
                    icon="exclamation circle"
                    content={t(
                      'survey.links_and_tools_active_once_previous_plan_closed'
                    )}
                  />
                </div>
              </div>
            ) : (
              <div className="m-4">
                <div className="msg-series-evaluation">
                  <Message
                    color="yellow"
                    icon="exclamation circle"
                    content={
                      t('survey.ensure_previous_in_series_closed')
                      // have been uploaded on the documentation page'
                    } // TODO: Need to check ?? previous evaluation one and show the message according to previous evaluation collection method
                  />
                </div>
              </div>
            )}
            {isSelfAssesed || isCultureCount ? null : (
              <div className="refer-target-guideline-row row">
                <ContentAndHeaderEvaluation>
                  {t('survey.use_survey_link_info')}
                </ContentAndHeaderEvaluation>
                <br />
                <p
                  className={
                    surveyValidation ? 'c-content-eval-bold' : 'c-content-eval'
                  }
                >
                  {t('survey.survey_links_instructions_1')}
                  <br />
                  {t('survey.survey_links_instructions_2')}
                </p>
              </div>
            )}
          </Fragment>
        )}
        <div className="greenlight-evaluation-plan-objective row">
          <div className="col-lg-6 greenlight-evaluation-plan-objective-extra-left-padding">
            <ContentAndHeaderEvaluation title={t('common.objective')}>
              {map(
                get(evalPlan, 'evaluationQuestions', []),
                (evaluation, i) => (
                  <span
                    className="greenlight-objective-eval-group-list"
                    key={i}
                  >
                    {get(evaluation, 'outcome.title', '-')}
                  </span>
                )
              )}
            </ContentAndHeaderEvaluation>
          </div>
          <div className="col-lg-6 greenlight-evaluation-plan-objective-extra-left-padding">
            <ContentAndHeaderEvaluation title={t('survey.evaluation_groups')}>
              {map(get(evalPlan, 'participantGroups', []), (evaluation, i) => (
                <span className="greenlight-objective-eval-group-list" key={i}>
                  {get(evaluation, 'targetGroup.name', '-')}
                </span>
              ))}
            </ContentAndHeaderEvaluation>
          </div>
        </div>
        <div className="evaluation-respondent-type-row row">
          <div className="obj-div col-lg-6">
            <ContentAndHeaderEvaluation
              title={t('survey.evaluation_respondent_type')}
            >
              {formatEvaluationResponseLabel(
                t,
                get(evalPlan, 'method.response', '-')
              )}
            </ContentAndHeaderEvaluation>
          </div>
          {isSelfAssesed ? null : (
            <div className="goal-div col-lg-6">
              <ContentAndHeaderEvaluation title={t('survey.evaluation_method')}>
                {get(evalPlan, 'method.method', '-')}
              </ContentAndHeaderEvaluation>
            </div>
          )}
        </div>
        {isSelfAssesed ? null : facilitatorNote ? (
          <Fragment>
            <div className="green-qs">
              <div
                onClick={() => setFacilityVisibility(!facilityVisibility)}
                className="show-color"
              >
                {facilityVisibility ? (
                  <Icon name="angle up" />
                ) : (
                  <Icon name="angle down" />
                )}
                {t('survey.facilitator_tips')}
              </div>
            </div>
            {facilityVisibility ? (
              <div className="question-and-objective-card row">
                <div
                  className="show-facilitiator-notes"
                  dangerouslySetInnerHTML={{
                    __html: UserTextService.replaceStdPlaceholders(
                      currentActivity,
                      evalPlan?.method?.facilitatorNote
                    ),
                  }}
                />
              </div>
            ) : null}
          </Fragment>
        ) : null}
        {!isSelfAssesed ? (
          <Fragment>
            <div className="green-qs">
              <div
                onClick={() => setQuestionVisibility(!questionVisibility)}
                className="show-color"
              >
                {questionVisibility ? (
                  <Icon name="angle up" />
                ) : (
                  <Icon name="angle down" />
                )}
                {t('survey.show_evaluation_question')}
              </div>
            </div>
            {questionVisibility ? (
              <div className="question-and-objective-card row">
                {map(evaluationQs, (question, i) => {
                  return (
                    <ShowEvaluationQuestion question={question} index={i} />
                  );
                })}
              </div>
            ) : null}
          </Fragment>
        ) : null}
      </div>
      <br />

      <GenerateQrModal {...currentActivity} evalPlan={evalPlan} />
    </>
  );
};

export default SurveyContainer;
